import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { provideNgxMask } from 'ngx-mask';
import { SolicitacaoService } from 'src/app/core/apis/solicitacao/solicitacao.service';
import { AuthService } from 'src/app/core/auth/service/auth.service';
import { Arquivo } from 'src/app/core/model/arquivo';
import { Titular } from 'src/app/core/model/titular';
import { ErrorHandlerService } from 'src/app/core/service/error-handler.service';
import { OrdemBuilderService } from 'src/app/core/service/ordem-builder.service';
import { AcaoButtonsComponent } from "src/app/shared/components/acao-buttons/acao-buttons.component";
import { ListaTitularesComponent } from 'src/app/shared/components/lista-titulares/lista-titulares.component';
import { NroProcessoInputComponent } from 'src/app/shared/components/nro-processo-input/nro-processo-input.component';
import { SlaSelectorComponent } from "src/app/shared/components/sla-selector/sla-selector.component";
import { Globals } from 'src/app/shared/helpers/globals';

@Component({
    selector: 'app-outras-solicitacao',
    templateUrl: './outras-solicitacao.component.html',
    styleUrls: ['./outras-solicitacao.component.css'],
    providers: [provideNgxMask()],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatToolbarModule,
    MatTableModule,
    MatMenuModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
    NroProcessoInputComponent,
    SlaSelectorComponent,
    AcaoButtonsComponent,
    ListaTitularesComponent
]
})
export class OutrasSolicitacaoComponent implements OnInit {

  MENSAGEM_DESCRICAO = "Determinações relacionadas a temas diversos, como Cadastro Positivo, Serasa Score, Serasa Limpa Nome, entre outros assuntos não especificados nas opções anteriores. (Descreva de forma clara e detalhada a solicitação ou determinação desejada.)";

  tokenIam: string;

  form: UntypedFormGroup;

  @ViewChild(MatTable) table: MatTable<Titular>;
  displayedColumns: string[] = ["nome", "nroDocumento", "action"];

  @ViewChild(NroProcessoInputComponent) nroProcessoInput!: NroProcessoInputComponent;
  @ViewChild(ListaTitularesComponent) listaTitularesComponent!: ListaTitularesComponent;

  titulares: Array<Titular>;

  arquivoAnexo: File | null = null;

  constructor(
    private globals: Globals,
    private solicitacaoService: SolicitacaoService,
    private ordemBuilderService: OrdemBuilderService,
    private errorHandler: ErrorHandlerService,
    private authService: AuthService,
    private router: Router,
    fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      nroProcesso: [''],
      nroSla: ["120", Validators.required],
      descricaoOrdem: ["", Validators.required]
    });

    this.titulares = [];
  }

  ngOnInit() {
    this.tokenIam = this.authService.accessToken;
    if (!this.tokenIam) {
      this.router.navigate(['login']);
    }
  }

  public onTitularesChange(titulares: Titular[]) {
    this.titulares = titulares;
  }

  public fileBrowseHandler(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.type === 'application/pdf')
        this.arquivoAnexo = file;
      else
        this.globals.showSnackBar('Apenas arquivos PDF são permitidos');
    }
  }

  public removeFile(): void {
    this.arquivoAnexo = null;
  }

  public async enviarOrdem() {
    if(!this.isFormValid()){
      this.touchForm();
      return;
    }
    this.globals.addToLoading("cadastrar-ordem");

    let arquivo: Arquivo;
    if (this.arquivoAnexo) {
      const base64 = await this.convertFileToBase64(this.arquivoAnexo);
      arquivo = {
        nomeArquivo: this.arquivoAnexo.name,
        base64: base64
      };
    }

    const ordem = this.ordemBuilderService.buildOutros(this.nroProcesso.value, this.titulares, this.form.value, arquivo);
    this.solicitacaoService.saveSolicitacao(ordem, this.tokenIam).subscribe({
      next: (response) => {
        this.globals.subtractFromLoading();
        this.globals.showSnackBar("Ordem enviada com sucesso!", ["snackbar-success"]);
        this.router.navigate(["/ordem"]);
      },
      error: (error) => {
        this.globals.subtractFromLoading();
        this.errorHandler.handle(error);
      }
    });
  }

  private touchForm() {
    this.form.markAllAsTouched();
    this.nroProcessoInput.control.markAsTouched();
    if(this.isTitularesEmpty()){
      this.listaTitularesComponent.nome.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.documento.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.form.markAllAsTouched();
    }
  }

  public isFormValid(): boolean{
    return this.form.valid && !this.isTitularesEmpty();
  }

  private isTitularesEmpty(): boolean{
    return this.titulares.length == 0;
  }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  }

  get nroProcesso() {
    return this.form.get("nroProcesso");
  }

  get nroSla() {
    return this.form.get("nroSla");
  }

  get descricaoOrdem() {
    return this.form.get("descricaoOrdem");
  }

}
